import type { DailyTrendPoint } from '@/lib/api/analytics';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * RYTM TYGODNIA — to, czego przebieg dzienny nie pokazuje
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Makieta ma na ekranie sprzedaży sekcję „Rytm tygodnia i granice tej liczby". Produkt jej nie
 * miał. To NIE jest przypadek wymagający nowego kontraktu — seria dzienna, którą ekran już czyta,
 * niesie datę każdego punktu, a dzień tygodnia jest funkcją daty. Wyliczamy więc rytm z danych,
 * które już są, i nie prosimy backendu o nic nowego.
 *
 * ── DLACZEGO ŚREDNIA NA WYSTĄPIENIE, A NIE SUMA ───────────────────────────────────────────────
 *
 * Suma po dniu tygodnia jest pułapką. W oknie 30-dniowym niektóre dni tygodnia wypadają pięć razy,
 * a inne cztery. Słupek zbudowany na sumie pokazałby wtedy poniedziałek o ćwierć wyższy od wtorku
 * wyłącznie dlatego, że poniedziałków było więcej — i czytelnik wyciągnąłby wniosek o zachowaniu
 * klientów z artefaktu kalendarza. Porównywalna jest ŚREDNIA NA WYSTĄPIENIE; sumę i liczbę
 * wystąpień oddajemy obok, żeby dało się to sprawdzić.
 *
 * ── DZIEŃ BEZ WYSTĄPIEŃ NIE JEST ZEREM ────────────────────────────────────────────────────────
 *
 * Jeżeli w oknie nie ma ani jednej soboty, sobota nie trafia do wyniku. Narysowanie jej jako
 * słupka o wysokości zero twierdziłoby, że w soboty nie sprzedajemy — a prawdą jest, że w tym
 * oknie soboty nie było. To ta sama reguła, którą całe Control Center trzyma w `pustyZbior.ts`.
 *
 * ── UTC JEST TU OBOWIĄZKOWE ───────────────────────────────────────────────────────────────────
 *
 * `new Date('2026-08-23')` daje północ UTC, ale `getDay()` czyta ją w strefie przeglądarki.
 * Na zachód od Greenwich dzień cofa się o jeden i cały rytm przesuwa się o dobę. Czytamy więc
 * `getUTCDay()` — data z backendu jest kalendarzowa, bez pory dnia, i nie wolno jej przesuwać.
 */

/** Kolejność NATURALNA, od poniedziałku. Nie jest to ranking i nie wolno tego sortować po wartości. */
const DNI = [
  'poniedziałek',
  'wtorek',
  'środa',
  'czwartek',
  'piątek',
  'sobota',
  'niedziela',
] as const;

export interface DzienTygodnia {
  /** Nazwa dnia, małą literą — tak jak w makiecie. */
  dzien: (typeof DNI)[number];
  /** Ile razy ten dzień tygodnia wystąpił w oknie. Nigdy 0 — dni bez wystąpień nie ma w wyniku. */
  wystapien: number;
  /** Suma wartości katalogowej wszystkich wystąpień tego dnia. */
  suma: number;
  /** Suma zamówień wszystkich wystąpień tego dnia. */
  zamowien: number;
  /** `suma / wystapien` — jedyna wielkość porównywalna między dniami tygodnia. */
  srednia: number;
}

/**
 * Zwraca `null`, gdy nie ma z czego liczyć rytmu. `null` znaczy „nie wiadomo", a nie „brak rytmu" —
 * wywołujący ma pokazać stan pusty, nie siedem zer.
 */
export function rytmTygodnia(punkty: readonly DailyTrendPoint[] | null | undefined): DzienTygodnia[] | null {
  if (!punkty || punkty.length === 0) return null;

  const kubelki = new Map<number, { wystapien: number; suma: number; zamowien: number }>();

  for (const p of punkty) {
    const ms = Date.parse(`${p.date}T00:00:00Z`);
    // Data nieparsowalna nie trafia do żadnego kubełka. Wrzucenie jej do poniedziałku
    // „bo trzeba gdzieś" zmieniłoby uszkodzony wiersz w cichy błąd pomiaru.
    if (!Number.isFinite(ms)) continue;

    // getUTCDay(): 0 = niedziela. Przesuwamy na indeks od poniedziałku.
    const indeks = (new Date(ms).getUTCDay() + 6) % 7;
    const biezacy = kubelki.get(indeks) ?? { wystapien: 0, suma: 0, zamowien: 0 };
    kubelki.set(indeks, {
      wystapien: biezacy.wystapien + 1,
      suma: biezacy.suma + p.sales_grosze,
      zamowien: biezacy.zamowien + p.orders,
    });
  }

  if (kubelki.size === 0) return null;

  const wynik: DzienTygodnia[] = [];
  for (let i = 0; i < DNI.length; i += 1) {
    const k = kubelki.get(i);
    if (!k) continue;
    wynik.push({
      dzien: DNI[i]!,
      wystapien: k.wystapien,
      suma: k.suma,
      zamowien: k.zamowien,
      srednia: k.suma / k.wystapien,
    });
  }

  return wynik.length ? wynik : null;
}

/**
 * Czy okno jest zrównoważone, czyli czy każdy obecny dzień tygodnia wystąpił tyle samo razy.
 *
 * Przy oknie niezrównoważonym średnia nadal jest poprawna, ale liczba wystąpień różni się między
 * dniami, więc pewność jest różna — i ekran ma to powiedzieć wprost, zamiast rysować siedem
 * słupków wyglądających na jednakowo wiarygodne.
 */
export function oknoZrownowazone(rytm: readonly DzienTygodnia[] | null): boolean {
  if (!rytm || rytm.length === 0) return false;
  const pierwszy = rytm[0]!.wystapien;
  return rytm.every((d) => d.wystapien === pierwszy);
}
